import * as Sentry from "@sentry/react";

import { call, put, retry, takeLatest } from "redux-saga/effects";

import { AxiosResponse } from "axios";
import { BASE_API_URL } from "../../config";
import { actions } from "./slice";
import { createRequest } from "../../utils/requestUtils";

export function* fetchSingleCase({
  payload,
}: ReturnType<typeof actions.requestSingleCase>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}`;

  try {
    const { data }: AxiosResponse = yield call(createRequest, {
      url: url,
    });

    yield put(actions.requestSingleCaseSuccessful(data.payload));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "fetchSingleCase");
    Sentry.setContext("saga", {
      name: "SingleCase",
      function: "fetchSingleCase",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to fetch single case",
    });

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestSingleCaseFailed(errorMessage));
  }
}

function* fetchDocumentsFromArchive({
  payload,
}: ReturnType<typeof actions.requestDocumentsFromArchive>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/documents`;

  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      url: url,
    });

    yield put(actions.requestDocumentsFromArchiveSuccessful(data.payload));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "fetchDocumentsFromArchive");
    Sentry.setContext("saga", {
      name: "SingleCase",
      function: "fetchDocumentsFromArchive",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to fetch documents from archive signing",
    });

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestDocumentsFromArchiveFailed(errorMessage));
  }
}

function* cancelCase({
  payload,
}: ReturnType<typeof actions.requestCancelCase>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/cancel`;

  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      method: "patch",
      url: url,
    });
    yield put(actions.requestCancelCaseSuccessful(data.payload));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "cancelCase");
    Sentry.setContext("saga", {
      name: "SingleCase",
      function: "cancelCase",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to cancel case",
    });

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestCancelCaseFailed(errorMessage));
  }
}

function* singleCaseSaga() {
  yield takeLatest(actions.requestSingleCase, fetchSingleCase);
  yield takeLatest(
    actions.requestDocumentsFromArchive,
    fetchDocumentsFromArchive,
  );
  yield takeLatest(actions.requestCancelCase, cancelCase);
}

export default singleCaseSaga;
